/**
 * Development-only persistence for Bird and Beakfest tree hole positions (localStorage).
 * Coordinates are logical canvas space (1920×1080), same as `beyondBirdMemoryLayout.js`.
 */

const STORAGE_KEY = 'exploreUnknownBeyondBirdTreeSlots';

export const BEYOND_BIRD_TREE_SLOT_COUNT = 5;

/**
 * @param {unknown} slots
 */
function assertValidSlots(slots) {
  if (!Array.isArray(slots) || slots.length !== BEYOND_BIRD_TREE_SLOT_COUNT) {
    throw new Error(
      `exploreUnknownBirdTreeDevStorage: slots must be an array of length ${BEYOND_BIRD_TREE_SLOT_COUNT}`
    );
  }
  for (let i = 0; i < slots.length; i += 1) {
    const s = slots[i];
    if (!s || typeof s !== 'object') {
      throw new Error(`exploreUnknownBirdTreeDevStorage: slot ${i} must be an object`);
    }
    if (s.id !== i) {
      throw new Error(`exploreUnknownBirdTreeDevStorage: slot ${i} must have id ${i}`);
    }
    const [x, y] = s.center;
    if (!Array.isArray(s.center) || s.center.length !== 2 || !Number.isFinite(x) || !Number.isFinite(y)) {
      throw new Error(`exploreUnknownBirdTreeDevStorage: slot ${i}.center must be [x, y]`);
    }
    if (!Number.isFinite(s.hitRadius) || s.hitRadius < 10) {
      throw new Error(`exploreUnknownBirdTreeDevStorage: slot ${i}.hitRadius must be >= 10`);
    }
  }
}

/**
 * @returns {Array<{ id: number, center: [number, number], hitRadius: number }> | null}
 */
export function loadDevBirdTreeSlots() {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    throw new Error(`exploreUnknownBirdTreeDevStorage: localStorage read failed (${err?.message ?? err})`);
  }
  if (raw == null || raw === '') {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`exploreUnknownBirdTreeDevStorage: stored JSON is invalid (${err?.message ?? err})`);
  }
  assertValidSlots(parsed);
  return parsed.map((s) => ({
    id: s.id,
    center: [s.center[0], s.center[1]],
    hitRadius: s.hitRadius,
  }));
}

/**
 * @param {Array<{ id: number, center: [number, number], hitRadius: number }>} slots
 */
export function saveDevBirdTreeSlots(slots) {
  if (typeof window === 'undefined') {
    throw new Error('exploreUnknownBirdTreeDevStorage: window is required');
  }
  assertValidSlots(slots);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  } catch (err) {
    throw new Error(`exploreUnknownBirdTreeDevStorage: localStorage write failed (${err?.message ?? err})`);
  }
}

export function clearDevBirdTreeSlots() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    throw new Error(`exploreUnknownBirdTreeDevStorage: localStorage remove failed (${err?.message ?? err})`);
  }
}

/**
 * @param {Array<{ id: number, center: [number, number], hitRadius: number }>} slots
 * @returns {string}
 */
export function formatBirdTreeSlotsForLayoutFile(slots) {
  assertValidSlots(slots);
  const lines = slots.map((s) => {
    const x = Math.round(s.center[0]);
    const y = Math.round(s.center[1]);
    const r = Math.round(s.hitRadius);
    return `  { id: ${s.id}, center: [${x}, ${y}], hitRadius: ${r} },`;
  });
  return `export const BEYOND_BIRD_MEMORY_TREE_SLOTS = [\n${lines.join('\n')}\n];`;
}
